import { Map, List } from 'immutable'
import { createSelector } from 'reselect'
import { last } from 'lodash'

import * as constants from './constants'
import { User } from './models'
import { GoogleScopes } from 'app/modules/integrations/constants'

import { getUserDisplayName } from 'app/modules/user/utils'
import { generateSettingsWebAppConfig, generateTrafficControlNavConfig } from '@driftt/app-config'

export const root = state => state.get('currentUser')

const orgUsers = state => root(state).get('orgUsers', Map())

export const currentUser = state => root(state).get('user')
const currentUserJS = state => !!currentUser(state) ? currentUser(state).toJS(): {}

export const isCurrentUserAdmin = state => root(state).get('user').scopes.includes('org_admin')
export const enrichment = state => root(state).get('enrichment')
export const currentUserStatus = state => root(state).getIn(['status', 'currentUser'])
export const gates = state => root(state).getIn(['user', 'gates'])

const userConvoLimits = state => root(state).get('userConvoLimits', Map())
export const convoLimitForUser = (state, userId) => userConvoLimits(state).getIn([userId, 'data'], {}).convoLimit
export const convoLimitForUserLoading = (state, userId) => userConvoLimits(state).getIn([userId, 'status']) === constants.FETCH_INDIVIDUAL_USER_CONVO_LIMIT_PENDING
export const convoLimitForUserNeverLoaded = (state, userId) => userConvoLimits(state).getIn([userId, 'status']) === undefined
export const convoLimitForUserSavedSuccess = (state, userId) => userConvoLimits(state).getIn([userId, 'status']) === constants.UPDATE_INDIVIDUAL_USER_CONVO_LIMIT_SUCCESS

export const currentGoogleScopesStatus = state => root(state).get('googleScopesStatus')

export const isGoogleScopesPending = state =>
  currentGoogleScopesStatus(state) === constants.FETCH_GOOGLE_SCOPES_PENDING

export const getGoogleScopes = state => root(state).get('googleScopes', List())

export const isGmailConnected = state => {
  const googleScopes = getGoogleScopes(state)
  return googleScopes.includes(GoogleScopes.READ) && googleScopes.includes(GoogleScopes.SEND)
}

export const getEmailSignature = state => state.getIn(['currentUser', 'emailSignature'])

// TODO: deprecated, remove when multi banner signatures is ready
const orgSignature = state => state.getIn(['currentUser', 'orgSignature'], Map())
export const orgSignatureStatus = state => orgSignature(state).get('status')
export const orgSignatureFetchPending = state => [constants.FETCH_ORG_SIGNATURE, constants.FETCH_ORG_SIGNATURE_PENDING].includes(orgSignatureStatus(state))
export const orgSignatureFetchFailed = state => orgSignatureStatus(state) === constants.FETCH_ORG_SIGNATURE_FAILED
export const orgSignatureContent = state => orgSignature(state).getIn(['data', 'emailSignature'])

const orgMultiSignature = state => state.getIn(['currentUser', 'orgMultiSignature'], Map())
export const orgMultiSignatureStatus = state => orgMultiSignature(state).get('status')
export const orgMultiSignatureFetchPending = state => [constants.FETCH_ORG_MULTI_SIGNATURE, constants.FETCH_ORG_MULTI_SIGNATURE_PENDING].includes(orgMultiSignatureStatus(state))
export const orgMultiSignatureFetchFailed = state => orgMultiSignatureStatus(state) === constants.FETCH_ORG_MULTI_SIGNATURE_FAILED
export const orgMultiSignatureData = state => orgMultiSignature(state).get('data', Map())

export const currentUserLoading = createSelector(
  currentUserStatus,
  status => status === constants.FETCH_CURRENT_USER_STARTED
)

export const currentUserLoaded = createSelector(
  currentUserStatus,
  status => status === constants.RECEIVE_CURRENT_USER
)

export const fetchCurrentUserFailed = createSelector(
  currentUserStatus,
  status => status === constants.PATCH_CURRENT_USER_FAILED
)

export const unfilteredOrgUsersById = createSelector(orgUsers, orgUsers =>
  orgUsers.map(user =>
    user.set(
      'name',
      user.status === constants.UserStatus.ARCHIVED ? `${getUserDisplayName(user)} [deleted]` : user.name
    )
  )
)

export const currentUserName = state => state.getIn(['currentUser', 'user', 'name'])
export const currentUserEmail = state => state.getIn(['currentUser', 'user', 'email'])
export const currentUserId = state => state.getIn(['currentUser', 'user', 'id'])
export const currentUserFirstName = state => currentUserName(state) && currentUserName(state).split(" ")[0]
export const currentUserLastName = state => currentUserName(state) && last(currentUserName(state).split(" "))

export const unfilteredOrgUsersByIdJS = createSelector(unfilteredOrgUsersById, unfilteredOrgUsersById =>
  unfilteredOrgUsersById.toJS()
)

export const orgUsersById = createSelector(
  unfilteredOrgUsersById,
  (unfilteredOrgUsersById) => unfilteredOrgUsersById.filter(user => user.status !== constants.UserStatus.ARCHIVED)
)

export const nonBotUsers = createSelector(
  orgUsersById,
  orgUsersById => orgUsersById.filter(user => !user.bot)
)

export const botUser = createSelector(orgUsersById, orgUsersById => orgUsersById.find(user => user.bot))

export const availableUsers = createSelector(
  nonBotUsers,
  nonBotUsers => nonBotUsers
    .filter(user => user.status === constants.UserStatus.ENABLED)
    .filter(user => user.availability !== constants.AvailabilityStatus.AWAY)
)

export const awayUsers = createSelector(
  nonBotUsers,
  nonBotUsers => nonBotUsers
    .filter(user => user.status === constants.UserStatus.ENABLED)
    .filter(user => user.availability === constants.AvailabilityStatus.AWAY)
)

export const makeUserSelector = () => createSelector(
  [
    unfilteredOrgUsersById,
    (state, props) => props.id
  ],
  (unfilteredOrgUsersById, id) => {
    const emptyUser = User({
      id,
      name: '[Deleted Team Member]',
      status: constants.UserStatus.ARCHIVED,
    })

    return unfilteredOrgUsersById.get(id, emptyUser)
  }
)

export const isUpdatingUser = createSelector(root, root => root.getIn(['status', 'updateUser']) === 'pending')
export const lastUpdatedUser = createSelector(root, root => root.getIn(['lastUpdatedUser']))


export const orgUsersStatus = createSelector(root, root => root.getIn(['status', 'orgUsers']))

export const orgUsersLoading = createSelector(orgUsersStatus, status => {
  return status === constants.FETCH_ORG_USERS_STARTED
})

export const orgUsersLoaded = createSelector(orgUsersStatus, status => {
  return status === constants.RECEIVE_ORG_USERS
})

export const currentOrgUsers = createSelector(orgUsersById, orgUsersById => orgUsersById.valueSeq())
export const currentNonBotOrgUsers = createSelector(nonBotUsers, orgUsersById => orgUsersById.valueSeq())
export const currentOrgUsersAlphabetical = createSelector(currentOrgUsers, orgUsers =>
  orgUsers.sortBy(user => (getUserDisplayName(user) || '').toLowerCase())
)
export const randomAndOrgUsersAlphabetical = createSelector(currentOrgUsers, users => List.of(User(constants.RANDOM_USER)).concat(users.sortBy(user => (getUserDisplayName(user) || '').toLowerCase())))
export const nonBotOrgUsersAlphabetical = createSelector(currentNonBotOrgUsers, orgUsers =>
  orgUsers.sortBy(user => (getUserDisplayName(user) || '').toLowerCase()),
)

export const numberOfOrgUsers = createSelector(currentOrgUsers, users => users.filter(user => !user.bot).count())

export const settingsAppConfig = createSelector(currentUserJS, currentUserJS => generateSettingsWebAppConfig(currentUserJS))

export const trafficControlConfig = createSelector(currentUserJS, currentUserJS => generateTrafficControlNavConfig(currentUserJS))



// WEBPACK FOOTER //
// ./src/scripts/app/modules/user/selectors.js